import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import Navbar from "../../ui/Navbar";
import Modal from "../../ui/Modal";
import pricingData from "./pricingData.json";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  FaPlus,
  FaTrash,
  FaSchool,
  FaChartLine,
  FaLayerGroup,
  FaClock,
  FaBolt,
  FaFileInvoiceDollar,
  FaBullseye,
  FaInfoCircle,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimes,
} from "react-icons/fa";
import { swalWarning } from "../../../utils/swalAlert";
import "../../../App.css";

const PriceCalculator = () => {
  const [tables, setTables] = useState([
    {
      id: Date.now(),
      plan: "lite",
      slab: "immediate",
      rows: [
        { id: Date.now() + 1, level: "", strength: "", subjects: "", testsPerSubject: "", customPrice: undefined },
      ],
    },
  ]);
  const [discount, setDiscount] = useState(pricingData.defaultDiscount);
  const [targetBudget, setTargetBudget] = useState("");
  // budgetFeedback: null | { type: 'error'|'info'|'success', message: string, details?: string[] }
  const [budgetFeedback, setBudgetFeedback] = useState(null);
  const [animatingRowId, setAnimatingRowId] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [quotationRecipient, setQuotationRecipient] = useState({
    name: "",
    designation: "Principal",
    schoolName: "",
    schoolAddress: "",
  });
  const logoRef = useRef(null);

  // Set page title and meta tags (replaces react-helmet)
  useEffect(() => {
    document.title = "Price Calculator \u2014 GradeMe AI (Internal)";
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.content = content;
    };
    setMeta("robots", "noindex, nofollow, noarchive, nosnippet");
    setMeta("googlebot", "noindex, nofollow");
  }, []);

  // Preload logo image for PDF
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = "/imgs/gmai-logo-crp.png";
    img.onload = () => {
      logoRef.current = img;
    };
  }, []);

  const getInheritedPrice = useCallback((plan, slab) => {
    return pricingData.pricing[plan]?.[slab] || 0;
  }, []);

  const getBenchmarkPrice = useCallback((plan, slab) => {
    const key = slab === "immediate" ? "immediateBenchmark" : "24hrsBenchmark";
    return pricingData.pricing[plan]?.[key] || 0;
  }, []);

  const calculateRow = useCallback(
    (row, tablePlan, tableSlab) => {
      const defaultPrice = getInheritedPrice(tablePlan, tableSlab);
      const pricePerAssessment = row.customPrice !== "" && row.customPrice !== undefined ? parseFloat(row.customPrice) : defaultPrice;

      const strength = parseInt(row.strength) || 0;
      const numSubjects = parseInt(row.subjects) || 0;
      const numTests = parseInt(row.testsPerSubject) || 0;
      const assessmentsPerStudent = numSubjects * numTests;
      const totalAssessments = strength * assessmentsPerStudent;

      const currentDiscount = parseFloat(discount) || 0;
      const discountedPricePerAssessment = pricePerAssessment * (1 - currentDiscount / 100);

      const grossPrice = totalAssessments * pricePerAssessment;
      const netPrice = totalAssessments * discountedPricePerAssessment;
      const discountAmount = grossPrice - netPrice;
      const pricePerStudent =
        strength > 0 ? (netPrice / strength).toFixed(2) : "0.00";
      return {
        assessmentsPerStudent,
        totalAssessments,
        grossPrice,
        discountAmount,
        netPrice,
        pricePerStudent,
        pricePerAssessment,
        discountedPricePerAssessment,
        defaultPrice,
      };
    },
    [discount, getInheritedPrice],
  );

  const totalPriceForSchool = useMemo(() => {
    return tables.reduce((sum, table) => {
      const tableSum = table.rows.reduce((rSum, row) => {
        const { netPrice } = calculateRow(row, table.plan, table.slab);
        return rSum + netPrice;
      }, 0);
      return sum + tableSum;
    }, 0);
  }, [tables, calculateRow]);

  const totalPriceBeforeDiscount = useMemo(() => {
    return tables.reduce((sum, table) => {
      const tableSum = table.rows.reduce((rSum, row) => {
        const { grossPrice } = calculateRow(row, table.plan, table.slab);
        return rSum + grossPrice;
      }, 0);
      return sum + tableSum;
    }, 0);
  }, [tables, calculateRow]);

  const totalStudents = useMemo(() => {
    return tables.reduce((sum, table) => {
      return sum + table.rows.reduce((rSum, row) => rSum + (parseInt(row.strength) || 0), 0);
    }, 0);
  }, [tables]);

  const totalAssessmentsAll = useMemo(() => {
    return tables.reduce((sum, table) => {
      return sum + table.rows.reduce((rSum, row) => {
        const { totalAssessments } = calculateRow(row, table.plan, table.slab);
        return rSum + totalAssessments;
      }, 0);
    }, 0);
  }, [tables, calculateRow]);

  // Detect rows with invalid custom prices (empty or below benchmark)
  // Also checks if discount % would push the effective price below benchmark
  const priceWarnings = useMemo(() => {
    const warnings = [];
    const currentDisc = parseFloat(discount) || 0;
    const discMult = 1 - currentDisc / 100;
    for (const table of tables) {
      const benchmark = getBenchmarkPrice(table.plan, table.slab);
      const defaultPrice = getInheritedPrice(table.plan, table.slab);
      for (const row of table.rows) {
        const hasCp = row.customPrice !== undefined;
        if (hasCp) {
          if (row.customPrice === "") {
            warnings.push({ tableId: table.id, rowId: row.id, message: "Price cannot be empty" });
            continue;
          }
          const num = parseFloat(row.customPrice);
          if (isNaN(num) || num < benchmark) {
            warnings.push({ tableId: table.id, rowId: row.id, message: `Min. price is ₹${benchmark}` });
            continue;
          }
          // Check if discount pushes this custom price below benchmark
          if (currentDisc > 0 && num * discMult < benchmark) {
            warnings.push({ tableId: table.id, rowId: row.id, message: `Discount makes price < ₹${benchmark}` });
            continue;
          }
        } else {
          // No custom price — check if discount pushes default price below benchmark
          if (currentDisc > 0 && defaultPrice * discMult < benchmark) {
            warnings.push({ tableId: table.id, rowId: row.id, message: `Discount makes price < ₹${benchmark}` });
          }
        }
      }
    }
    return warnings;
  }, [tables, discount, getBenchmarkPrice, getInheritedPrice]);

  const hasInvalidPrices = priceWarnings.length > 0;

  const addTable = () => {
    setTables([
      ...tables,
      {
        id: Date.now(),
        plan: "lite",
        slab: "immediate",
        rows: [{ id: Date.now() + 1, level: "", strength: "", subjects: "", testsPerSubject: "", customPrice: undefined }],
      },
    ]);
  };

  const removeTable = (tableId) => {
    if (tables.length === 1) return;
    setTables(tables.filter((t) => t.id !== tableId));
  };

  const updateTableConfig = (tableId, field, value) => {
    setTables(
      tables.map((t) => (t.id === tableId ? { ...t, [field]: value } : t)),
    );
  };

  const addRow = (tableId) => {
    const newRow = {
      id: Date.now(),
      level: "",
      strength: "",
      subjects: "",
      testsPerSubject: "",
      customPrice: undefined,
    };
    setTables(
      tables.map((t) =>
        t.id === tableId ? { ...t, rows: [...t.rows, newRow] } : t,
      ),
    );
    setAnimatingRowId(newRow.id);
    setTimeout(() => setAnimatingRowId(null), 500);
  };

  const removeRow = (tableId, rowId) => {
    setTables(
      tables.map((t) => {
        if (t.id === tableId) {
          if (t.rows.length === 1) return t;
          return { ...t, rows: t.rows.filter((r) => r.id !== rowId) };
        }
        return t;
      }),
    );
  };

  const updateRow = (tableId, rowId, field, value) => {
    setTables(
      tables.map((t) => {
        if (t.id === tableId) {
          return {
            ...t,
            rows: t.rows.map((r) =>
              r.id === rowId ? { ...r, [field]: value } : r,
            ),
          };
        }
        return t;
      }),
    );
  };

  const fitToBudget = useCallback(() => {
    const target = parseFloat(targetBudget);
    if (!target || target <= 0) {
      setBudgetFeedback({ type: 'error', message: 'Please enter a valid target budget.' });
      return;
    }
    const currentDiscount = parseFloat(discount) || 0;
    const discountMultiplier = 1 - currentDiscount / 100;

    // Gather per-row info
    const rowInfos = [];
    for (const table of tables) {
      const defaultPrice = getInheritedPrice(table.plan, table.slab);
      const benchmarkPrice = getBenchmarkPrice(table.plan, table.slab);
      for (const row of table.rows) {
        const numTests = parseInt(row.testsPerSubject) || 0;
        const numSubjects = parseInt(row.subjects) || 0;
        const strength = parseInt(row.strength) || 0;
        const currentPPA =
          row.customPrice !== "" && row.customPrice !== undefined
            ? parseFloat(row.customPrice)
            : defaultPrice;
        const totalAssessments = strength * numSubjects * numTests;
        rowInfos.push({
          tableId: table.id,
          rowId: row.id,
          numTests,
          numSubjects,
          strength,
          currentPPA,
          benchmarkPrice,
          totalAssessments,
        });
      }
    }

    // Check if there's anything to optimize
    const hasAssessments = rowInfos.some((r) => r.totalAssessments > 0);
    if (!hasAssessments) {
      setBudgetFeedback({ type: 'error', message: 'Please fill in Strength, Subjects, and Tests in at least one row.' });
      return;
    }

    // Current total (net of discount)
    const currentTotal = rowInfos.reduce(
      (sum, r) => sum + r.totalAssessments * r.currentPPA * discountMultiplier,
      0
    );

    // If already within budget, no changes needed
    if (currentTotal <= target) {
      setBudgetFeedback({ type: 'success', message: 'Already within budget — no changes needed!' });
      return;
    }

    // ── PHASE 1: Reduce Price Per Assessment via binary search ──
    // Compute total at all-benchmark (floor) prices with current tests
    const floorTotal = rowInfos.reduce(
      (sum, r) => sum + r.totalAssessments * r.benchmarkPrice * discountMultiplier,
      0
    );

    if (floorTotal <= target) {
      // We can solve it by reducing prices alone — binary search for scaling factor r
      let rLow = 0;
      let rHigh = 1;

      const computeTotal = (r) => {
        return rowInfos.reduce((sum, ri) => {
          const newPPA = Math.max(ri.benchmarkPrice, ri.currentPPA * r);
          return sum + ri.totalAssessments * newPPA * discountMultiplier;
        }, 0);
      };

      // Binary search: find the highest r such that computeTotal(r) <= target
      for (let i = 0; i < 100; i++) {
        const rMid = (rLow + rHigh) / 2;
        if (computeTotal(rMid) <= target) {
          rLow = rMid;
        } else {
          rHigh = rMid;
        }
      }

      // Use rLow — the highest r that still fits within the budget
      const finalR = rLow;
      setTables((prev) =>
        prev.map((t) => ({
          ...t,
          rows: t.rows.map((r) => {
            const info = rowInfos.find(
              (ri) => ri.tableId === t.id && ri.rowId === r.id
            );
            if (!info) return r;
            const newPPA = Math.max(
              info.benchmarkPrice,
              Math.round(info.currentPPA * finalR * 100) / 100
            );
            return { ...r, customPrice: String(newPPA) };
          }),
        }))
      );
      setBudgetFeedback({
        type: 'success',
        message: 'Budget fitted successfully!',
        details: ['Price per assessment was reduced across all rows to meet the target budget.'],
      });
      return;
    }

    // ── PHASE 2: Prices at floor, now also reduce Tests Per Subject ──
    const maxTests = Math.max(...rowInfos.map((r) => r.numTests), 0);
    if (maxTests <= 0) {
      setBudgetFeedback({ type: 'error', message: 'Budget too low — cannot meet this amount even at minimum pricing.' });
      return;
    }

    for (let candidate = maxTests; candidate >= 1; candidate--) {
      let totalPrice = 0;
      for (const r of rowInfos) {
        const effectiveTests = Math.min(r.numTests, candidate);
        const assessments = r.numSubjects * effectiveTests;
        totalPrice += r.strength * assessments * r.benchmarkPrice * discountMultiplier;
      }
      if (totalPrice <= target) {
        // Collect info about what changed for the notification
        const changedRows = [];
        for (const ri of rowInfos) {
          const wasCapped = ri.numTests > candidate;
          if (wasCapped) {
            const table = tables.find((t) => t.id === ri.tableId);
            const row = table?.rows.find((r) => r.id === ri.rowId);
            const levelName = row?.level || `Grade`;
            changedRows.push({ level: levelName, from: ri.numTests, to: candidate });
          }
        }

        // Apply: set all prices to benchmark AND cap tests
        setTables((prev) =>
          prev.map((t) => ({
            ...t,
            rows: t.rows.map((r) => {
              const info = rowInfos.find(
                (ri) => ri.tableId === t.id && ri.rowId === r.id
              );
              if (!info) return r;
              const currentTests = parseInt(r.testsPerSubject) || 0;
              return {
                ...r,
                customPrice: String(info.benchmarkPrice),
                testsPerSubject:
                  currentTests > candidate ? String(candidate) : r.testsPerSubject,
              };
            }),
          }))
        );

        // Build user-friendly details
        const details = [
          'Price per assessment was set to the minimum allowed (benchmark price).',
        ];
        if (changedRows.length > 0) {
          const cappedSummary = changedRows.map(
            (c) => `${c.level}: ${c.from} → ${c.to} tests/subject`
          );
          details.push(
            `No. of tests per subject was reduced to fit the budget:`,
            ...cappedSummary.map((s) => `  • ${s}`)
          );

          swalWarning("Budget fitted — with adjustments to tests per subject", {
            title: "Notice: Tests Reduced",
            html: `<div style="text-align: left; font-size: 0.9em; line-height: 1.5;">
              <p style="margin-bottom: 8px;">Price per assessment was set to the minimum allowed.</p>
              <p style="margin-bottom: 5px;"><strong>To meet your budget, the number of tests per subject was also reduced:</strong></p>
              <ul style="margin: 0 0 0 20px; padding: 0;">
                ${cappedSummary.map((s) => `<li style="margin-bottom: 3px;">${s}</li>`).join("")}
              </ul>
            </div>`,
            showConfirmButton: true,
            timer: undefined,
          });
        }

        setBudgetFeedback({
          type: 'info',
          message: 'Budget fitted — with adjustments to tests per subject',
          details,
        });
        return;
      }
    }

    setBudgetFeedback({
      type: 'error',
      message: 'Budget too low — cannot meet this amount even at minimum pricing and 1 test per subject.',
    });
  }, [targetBudget, tables, discount, getInheritedPrice, getBenchmarkPrice]);

  const handleDownloadPdf = () => {
    setIsGeneratingPdf(true);
    setTimeout(async () => {
      try {
        const element = document.getElementById("pdf-content");
        if (!element) return;

        const originalWidth = element.style.width;
        element.style.width = "1200px";

        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#f8f9fa",
          logging: false,
          windowWidth: 1200,
        });

        element.style.width = originalWidth;

        const imgData = canvas.toDataURL("image/png");

        const pageWidth = 841.89;
        const margin = 30;
        const usableWidth = pageWidth - margin * 2;

        const imgHeight = (canvas.height * usableWidth) / canvas.width;
        const pageHeight = imgHeight + margin * 2;

        const pdf = new jsPDF("l", "pt", [pageWidth, pageHeight]);
        pdf.addImage(imgData, "PNG", margin, margin, usableWidth, imgHeight);
        pdf.save("GradeMe_AI_Price_Estimate.pdf");
      } catch (err) {
        console.error("Failed to generate PDF", err);
      } finally {
        setIsGeneratingPdf(false);
      }
    }, 200);
  };

  const formatCurrencyPdf = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case "lite":
        return <FaLayerGroup />;
      case "standard":
        return <FaChartLine />;
      case "pro":
        return <FaBolt />;
      default:
        return <FaLayerGroup />;
    }
  };

  const getSlabIcon = (slabId) => {
    return slabId === "immediate" ? <FaBolt /> : <FaClock />;
  };

  const breadcrumbs = [
    { name: "Admin", path: "/admin", id: "admin" },
    {
      name: "Price Calculator",
      path: "/admin/price-calculator",
      id: "price-calculator",
      isLast: true,
    },
  ];

  const handleGenerateQuotation = async () => {
    setIsGeneratingPdf(true);
    try {
      const pdf = new jsPDF("p", "pt", "a4");

      const fetchFont = async (url, vfsName, fontName, fontStyle) => {
        const resp = await fetch(url);
        const buffer = await resp.arrayBuffer();
        let binary = "";
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64 = window.btoa(binary);
        pdf.addFileToVFS(vfsName, base64);
        pdf.addFont(vfsName, fontName, fontStyle);
      };

      await fetchFont(
        "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf",
        "Roboto-Regular.ttf",
        "Roboto",
        "normal",
      );
      await fetchFont(
        "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf",
        "Roboto-Medium.ttf",
        "Roboto",
        "bold",
      );

      const W = pdf.internal.pageSize.getWidth(); // 595.28
      const H = pdf.internal.pageSize.getHeight(); // 841.89
      const M = 50; // margin
      const cW = W - M * 2; // content width
      let y = M;

      const today = new Date();
      const dateStr = today.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
      const refNo = `GMAI-${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;

      // ── Brand Header Bar ──
      pdf.setFillColor(240, 154, 33);
      pdf.rect(0, 0, W, 6, "F");

      // ── Company Logo + Name ──
      y = 35;
      if (logoRef.current) {
        pdf.addImage(logoRef.current, "PNG", M, y - 10, 30, 30);
      }
      const logoOffset = logoRef.current ? 38 : 0;
      pdf.setFont("Roboto", "bold");
      pdf.setFontSize(24);
      pdf.setTextColor(0, 0, 0);
      pdf.text("GradeMe AI", M + logoOffset, y + 13);

      // ── QUOTATION Title ──
      pdf.setFontSize(22);
      pdf.setTextColor(50, 50, 50);
      pdf.text("Quotation", W - M, y + 13, { align: "right" });

      // ── Thin line under header ──
      y += 24;
      pdf.setDrawColor(230, 230, 230);
      pdf.setLineWidth(1);
      pdf.line(M, y, W - M, y);

      // ── Date & Ref ──
      y += 20;
      pdf.setFontSize(8.5);
      pdf.setTextColor(130, 130, 130);
      pdf.setFont("Roboto", "normal");
      pdf.text(`Date: ${dateStr}`, M, y);
      pdf.text(`Ref: ${refNo}`, W - M, y, { align: "right" });

      // ── FROM / TO ──
      y += 28;
      const colFromX = M;
      const colToX = W / 2 + 20;

      // FROM
      pdf.setFont("Roboto", "bold");
      pdf.setFontSize(7.5);
      pdf.setTextColor(160, 160, 160);
      pdf.text("FROM", colFromX, y);
      y += 14;
      pdf.setFontSize(10.5);
      pdf.setTextColor(40, 40, 40);
      pdf.setFont("Roboto", "bold");
      pdf.text("Snehith M.", colFromX, y);
      y += 14;
      pdf.setFont("Roboto", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(90, 90, 90);
      pdf.text("Head of Business", colFromX, y);
      y += 13;
      pdf.text("Arvanch Software Solutions,", colFromX, y);
      y += 13;
      pdf.text("Rajendranagar, Hyderabad", colFromX, y);
      y += 13;
      const phoneText = "Mob. No.: +91-91214 15891";
      const separatorText = "  |  ";
      const emailText = "Email: snehith@grademe-ai.com";

      pdf.setTextColor(0, 102, 204); // Blue link color
      pdf.textWithLink(phoneText, colFromX, y, { url: "tel:+919121415891" });

      const phoneWidth = pdf.getTextWidth(phoneText);
      pdf.setTextColor(90, 90, 90);
      pdf.text(separatorText, colFromX + phoneWidth, y);

      const sepWidth = pdf.getTextWidth(separatorText);
      pdf.setTextColor(0, 102, 204); // Blue link color
      pdf.textWithLink(emailText, colFromX + phoneWidth + sepWidth, y, {
        url: "mailto:snehith@grademe-ai.com",
      });

      // TO (same baseline as FROM)
      let ty = y - 65;
      pdf.setFont("Roboto", "bold");
      pdf.setFontSize(7.5);
      pdf.setTextColor(160, 160, 160);
      pdf.text("TO", colToX, ty);
      ty += 14;
      pdf.setFontSize(10.5);
      pdf.setTextColor(40, 40, 40);
      pdf.setFont("Roboto", "bold");
      pdf.text(quotationRecipient.name || "—", colToX, ty);
      ty += 14;
      pdf.setFont("Roboto", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(90, 90, 90);
      pdf.text(quotationRecipient.designation || "", colToX, ty);
      ty += 13;
      pdf.text(quotationRecipient.schoolName || "—", colToX, ty);
      if (quotationRecipient.schoolAddress) {
        const addrLines = pdf.splitTextToSize(
          quotationRecipient.schoolAddress,
          cW / 2 - 20,
        );
        addrLines.forEach((line) => {
          ty += 13;
          pdf.text(line, colToX, ty);
        });
      }

      y += 32;

      // ── Tables ──
      tables.forEach((table, tIdx) => {
        if (y > H - 150) {
          pdf.addPage();
          y = M + 20;
        }

        const planName = pricingData.plans.find((p) => p.id === table.plan)?.name || table.plan;
        const slabName = pricingData.slabs.find((s) => s.id === table.slab)?.name || table.slab;

        // Plan Info Strip
        pdf.setFillColor(248, 249, 250);
        pdf.roundedRect(M, y - 4, cW, 28, 4, 4, "F");
        pdf.setFontSize(8.5);
        pdf.setFont("Roboto", "bold");
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Plan: ${planName}`, M + 12, y + 12);
        pdf.text(`SLA: ${slabName}`, M + 160, y + 12);

        y += 44;

        const cols = [
          { label: "#", w: 28, align: "center" },
          { label: "Level", w: 45, align: "left" },
          { label: "Strength", w: 62, align: "right" },
          { label: "Subjects", w: 56, align: "right" },
          { label: "Tests/Subj", w: 60, align: "right" },
          { label: "Assess./Student", w: 80, align: "right" },
          { label: "Total Assess.", w: 72, align: "right" },
          { label: "Price/Student", w: 76, align: "right" },
          { label: "Total Price", w: 80, align: "right" },
        ];
        const totalTableW = cols.reduce((s, c) => s + c.w, 0);
        const tableOffsetX = M + (cW - totalTableW) / 2;

        // Table Header
        pdf.setFillColor(240, 154, 33);
        pdf.roundedRect(tableOffsetX, y - 4, totalTableW, 22, 3, 3, "F");
        pdf.setFontSize(7.5);
        pdf.setFont("Roboto", "bold");
        pdf.setTextColor(255, 255, 255);
        let cx = tableOffsetX;
        cols.forEach((col) => {
          const tx = col.align === "right" ? cx + col.w - 6 : col.align === "center" ? cx + col.w / 2 : cx + 6;
          pdf.text(col.label, tx, y + 10, { align: col.align === "left" ? "left" : col.align });
          cx += col.w;
        });

        y += 22;

        const validRows = table.rows.filter(r => r.level || parseInt(r.strength) > 0);
        const dataRows = validRows.length > 0 ? validRows : table.rows;

        dataRows.forEach((row, idx) => {
          if (y > H - M - 30) {
            pdf.addPage();
            y = M + 20;
            // Draw header again
            pdf.setFillColor(240, 154, 33);
            pdf.roundedRect(tableOffsetX, y - 4, totalTableW, 22, 3, 3, "F");
            pdf.setFontSize(7.5);
            pdf.setFont("Roboto", "bold");
            pdf.setTextColor(255, 255, 255);
            let hcx = tableOffsetX;
            cols.forEach((col) => {
              const tx = col.align === "right" ? hcx + col.w - 6 : col.align === "center" ? hcx + col.w / 2 : hcx + 6;
              pdf.text(col.label, tx, y + 10, { align: col.align === "left" ? "left" : col.align });
              hcx += col.w;
            });
            y += 22;
          }

          const calc = calculateRow(row, table.plan, table.slab);
          const rowY = y;

          if (idx % 2 === 0) {
            pdf.setFillColor(250, 250, 250);
            pdf.rect(tableOffsetX, rowY - 2, totalTableW, 24, "F");
          }

          pdf.setFontSize(8.5);
          pdf.setFont("Roboto", "normal");
          pdf.setTextColor(60, 60, 60);

          const rowData = [
            { text: String(idx + 1), align: "center" },
            { text: row.level || "—", align: "left" },
            { text: (parseInt(row.strength) || 0).toLocaleString("en-IN"), align: "right" },
            { text: (parseInt(row.subjects) || 0).toLocaleString("en-IN"), align: "right" },
            { text: (parseInt(row.testsPerSubject) || 0).toLocaleString("en-IN"), align: "right" },
            { text: calc.assessmentsPerStudent.toLocaleString("en-IN"), align: "right" },
            { text: calc.totalAssessments.toLocaleString("en-IN"), align: "right" },
            { text: formatCurrencyPdf(parseFloat(calc.pricePerStudent)), align: "right" },
            { text: formatCurrencyPdf(calc.netPrice), align: "right" },
          ];

          let rx = tableOffsetX;
          rowData.forEach((cell, ci) => {
            const tx = cell.align === "right" ? rx + cols[ci].w - 6 : cell.align === "center" ? rx + cols[ci].w / 2 : rx + 6;
            pdf.text(cell.text, tx, rowY + 12, { align: cell.align === "left" ? "left" : cell.align });
            rx += cols[ci].w;
          });
          y += 24;
        });

        y += 4;
        // Bottom line of the table
        pdf.setDrawColor(230, 230, 230);
        pdf.setLineWidth(0.5);
        pdf.line(tableOffsetX, y, tableOffsetX + totalTableW, y);
        y += 20; // space before next table
      });

      // ── Summary Section ──
      if (y > H - 180) {
        pdf.addPage();
        y = M + 20;
      }

      const summaryX = M + cW - 220; // align right
      const summaryRows = [
        ["Total Students", totalStudents.toLocaleString("en-IN")],
        ["Total Assessments", totalAssessmentsAll.toLocaleString("en-IN")],
      ];

      // Compute gross total at DEFAULT (inherited) prices — no custom price, no discount
      const grossAtDefault = tables.reduce((sum, table) => {
        const defPrice = getInheritedPrice(table.plan, table.slab);
        return sum + table.rows.reduce((rSum, row) => {
          const strength = parseInt(row.strength) || 0;
          const numSubjects = parseInt(row.subjects) || 0;
          const numTests = parseInt(row.testsPerSubject) || 0;
          return rSum + strength * numSubjects * numTests * defPrice;
        }, 0);
      }, 0);

      const totalDiscount = grossAtDefault - totalPriceForSchool;
      if (totalDiscount > 0.5) {
        const effectiveDiscountPct = ((totalDiscount / grossAtDefault) * 100).toFixed(1);
        summaryRows.push([
          "Gross Amount",
          formatCurrencyPdf(Math.round(grossAtDefault)),
        ]);
        summaryRows.push([
          `Discount (${effectiveDiscountPct}%)`,
          `- ${formatCurrencyPdf(Math.round(totalDiscount))}`,
        ]);
      }

      pdf.setFontSize(9);
      summaryRows.forEach(([label, value]) => {
        pdf.setFont("Roboto", "normal");
        pdf.setTextColor(100, 100, 100);
        pdf.text(label, summaryX, y);
        pdf.setFont("Roboto", "bold");
        pdf.setTextColor(50, 50, 50);
        pdf.text(value, summaryX + 220, y, { align: "right" });
        y += 18;
      });

      // ── Total Amount ──
      y += 4;
      pdf.setFillColor(240, 154, 33);
      pdf.roundedRect(summaryX - 8, y - 12, 236, 30, 4, 4, "F");
      pdf.setFontSize(10);
      pdf.setFont("Roboto", "bold");
      pdf.setTextColor(255, 255, 255);
      pdf.text("Net Amount", summaryX, y + 6);
      pdf.setFontSize(13);
      pdf.text(
        formatCurrencyPdf(Math.round(totalPriceForSchool)),
        summaryX + 220,
        y + 6,
        { align: "right" },
      );

      // ── Terms & Conditions ──
      y += 52;
      if (y > H - 80) {
        pdf.addPage();
        y = M + 20;
      }
      pdf.setFontSize(8);
      pdf.setFont("Roboto", "bold");
      pdf.setTextColor(130, 130, 130);
      pdf.text("TERMS & CONDITIONS", M, y);
      y += 14;
      pdf.setFont("Roboto", "normal");
      pdf.setFontSize(7.5);
      pdf.setTextColor(140, 140, 140);
      const terms = [
        "1. This quotation is valid for 30 days from the date of issue.",
        "2. All prices are in Indian Rupees (INR) and are inclusive of applicable taxes.",
        "3. Payment can be made in full upfront or via monthly EMI, with credits applied accordingly.",
        "4. An advance payment of 10% of the billed amount is required upon signing the MOU, this amount is non-refundable.",
        "5. This is a system-generated document and does not require a physical signature.",
      ];
      terms.forEach((t) => {
        pdf.text(t, M, y);
        y += 12;
      });

      // ── Footer ──
      const footerY = H - 30;
      pdf.setDrawColor(240, 154, 33);
      pdf.setLineWidth(1);
      pdf.line(M, footerY - 10, W - M, footerY - 10);
      pdf.setFontSize(7);
      pdf.setTextColor(160, 160, 160);
      pdf.setFont("Roboto", "normal");
      pdf.text(
        "GradeMe AI  •  AI Assistant for Teachers  •  www.grademe-ai.com",
        W / 2,
        footerY,
        { align: "center" },
      );

      // ── Bottom accent bar ──
      pdf.setFillColor(240, 154, 33);
      pdf.rect(0, H - 6, W, 6, "F");

      pdf.save(
        `Quotation_${(quotationRecipient.schoolName || "School").replace(/\s+/g, "_")}_${refNo}.pdf`,
      );
      setShowQuotationModal(false);
    } catch (e) {
      console.error("Failed to generate Quotation PDF", e);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <>
      <Navbar breadcrumbs={breadcrumbs} />

      <div className="pc-page">
        <div
          className="pc-container"
          id="pdf-content"
          style={{ background: "transparent" }}
        >
          {tables.map((table, tIndex) => (
            <div key={table.id} className="pc-table-group" style={{ marginBottom: "3rem" }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', padding: '0 0.25rem' }}>
                <h2 style={{ fontSize: '1.4rem', color: 'var(--font-color)', margin: 0, fontFamily: 'var(--heading-font)', fontWeight: 700 }}>
                  Table {tIndex + 1} Configuration
                </h2>
                {tables.length > 1 && !isGeneratingPdf && (
                  <button 
                    onClick={() => removeTable(table.id)}
                    className="pc-remove-btn"
                    style={{ width: 'auto', padding: '0 1rem', display: 'flex', gap: '0.5rem' }}
                  >
                    <FaTrash /> Remove Table
                  </button>
                )}
              </div>

              {/* Configuration Cards for this Table */}
              <div className="pc-config-grid">
                {/* Plan Selection */}
                <div className="pc-config-card">
                  <label className="pc-config-label">
                    <FaLayerGroup className="pc-label-icon" />
                    Pricing Plan
                  </label>
                  <div className="pc-plan-selector">
                    {pricingData.plans.map((plan) => (
                      <button
                        key={plan.id}
                        className={`pc-plan-btn ${
                          table.plan === plan.id ? "active" : ""
                        }`}
                        onClick={() => updateTableConfig(table.id, "plan", plan.id)}
                      >
                        <span className="pc-plan-btn-icon">
                          {getPlanIcon(plan.id)}
                        </span>
                        <span className="pc-plan-btn-name">{plan.name}</span>
                      </button>
                    ))}
                  </div>
                  <p className="pc-config-desc">
                    {pricingData.plans.find((p) => p.id === table.plan)?.description || ""}
                  </p>
                </div>

                {/* SLA Selection */}
                <div className="pc-config-card">
                  <label className="pc-config-label">
                    <FaClock className="pc-label-icon" />
                    SLA (Slab)
                  </label>
                  <div className="pc-plan-selector">
                    {pricingData.slabs.map((slab) => (
                      <button
                        key={slab.id}
                        className={`pc-plan-btn pc-slab-btn ${
                          table.slab === slab.id ? "active" : ""
                        }`}
                        onClick={() => updateTableConfig(table.id, "slab", slab.id)}
                      >
                        <span className="pc-plan-btn-icon">
                          {getSlabIcon(slab.id)}
                        </span>
                        <span className="pc-plan-btn-name">{slab.name}</span>
                      </button>
                    ))}
                  </div>
                  <p className="pc-config-desc">
                    {pricingData.slabs.find((s) => s.id === table.slab)?.description || ""}
                  </p>
                </div>
              </div>

              {/* Calculator Table */}
              <div className="pc-table-section">
                <div className="pc-table-header">
                  <h2>
                    <FaSchool className="pc-section-icon" /> Level-wise Breakdown
                  </h2>
                </div>
                <div className="pc-table-wrapper">
                  <table className="pc-table">
                    <thead>
                      <tr>
                        <th className="pc-th-sno">#</th>
                        <th className="pc-th-level">Level (Grade)</th>
                        <th className="pc-th-strength">
                          Strength
                          <span className="pc-th-sub">(Students)</span>
                        </th>
                        <th>
                          Subjects
                        </th>
                        <th>
                          Tests
                          <span className="pc-th-sub">(Per Subject)</span>
                        </th>
                        <th className="pc-th-assessments">
                          Assessments
                          <span className="pc-th-sub">(Per Student)</span>
                        </th>
                        <th className="pc-th-price-per-assessment">
                          Price/Assessment
                          <span className="pc-th-sub">({"\u20b9"})</span>
                        </th>
                        <th className="pc-th-total-assessments">
                          Total
                          <span className="pc-th-sub">Assessments</span>
                        </th>
                        <th className="pc-th-price-student">
                          Price Per Student
                        </th>
                        <th className="pc-th-total">
                          Total Price
                        </th>
                        <th className="pc-th-action"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {table.rows.map((row, idx) => {
                        const calc = calculateRow(row, table.plan, table.slab);
                        return (
                          <tr
                            key={row.id}
                            className={`pc-table-row ${
                              animatingRowId === row.id ? "pc-row-enter" : ""
                            }`}
                          >
                            <td className="pc-td-sno">{idx + 1}</td>
                            <td>
                              <select
                                className="pc-select"
                                value={row.level}
                                onChange={(e) =>
                                  updateRow(table.id, row.id, "level", e.target.value)
                                }
                              >
                                <option value="">Select Grade</option>
                                {pricingData.levels.map((level) => (
                                  <option key={level} value={level}>
                                    {level}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                type="number"
                                className="pc-input"
                                placeholder="0"
                                min="0"
                                value={row.strength}
                                onChange={(e) =>
                                  updateRow(table.id, row.id, "strength", e.target.value)
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="pc-input"
                                placeholder="0"
                                min="0"
                                value={row.subjects}
                                onChange={(e) =>
                                  updateRow(table.id, row.id, "subjects", e.target.value)
                                }
                                style={{ width: '70px' }}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="pc-input"
                                placeholder="0"
                                min="0"
                                value={row.testsPerSubject}
                                onChange={(e) =>
                                  updateRow(table.id, row.id, "testsPerSubject", e.target.value)
                                }
                                style={{ width: '70px' }}
                              />
                            </td>
                            <td className="pc-td-computed" style={{ fontWeight: 700, color: 'var(--color-1)' }}>
                              {calc.assessmentsPerStudent.toLocaleString("en-IN")}
                            </td>
                            <td>
                              {(parseFloat(discount) || 0) > 0 ? (
                                <>
                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.15rem' }}>
                                  <span className="pc-price-original">
                                    ₹{calc.pricePerAssessment.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                  <span className={`pc-price-discounted${priceWarnings.some(w => w.tableId === table.id && w.rowId === row.id) ? '' : ''}`} style={priceWarnings.some(w => w.tableId === table.id && w.rowId === row.id) ? { color: 'var(--color-4, #ef4136)' } : {}}>
                                    ₹{calc.discountedPricePerAssessment.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                                {priceWarnings.find(w => w.tableId === table.id && w.rowId === row.id) && (
                                  <span style={{ fontSize: '0.65rem', color: 'var(--color-4, #ef4136)', fontWeight: 600, marginTop: '2px', lineHeight: 1.2, display: 'block' }}>
                                    {priceWarnings.find(w => w.tableId === table.id && w.rowId === row.id).message}
                                  </span>
                                )}
                                </>
                              ) : (
                                <>
                                <input
                                  type="number"
                                  className={`pc-input pc-price-input${priceWarnings.some(w => w.tableId === table.id && w.rowId === row.id) ? ' pc-price-invalid' : ''}`}
                                  placeholder={calc.defaultPrice}
                                  step="0.01"
                                  value={row.customPrice !== undefined ? row.customPrice : calc.defaultPrice}
                                  onChange={(e) => {
                                    updateRow(table.id, row.id, "customPrice", e.target.value);
                                  }}
                                  style={{ width: '80px' }}
                                />
                                {priceWarnings.find(w => w.tableId === table.id && w.rowId === row.id) && (
                                  <span style={{ fontSize: '0.65rem', color: 'var(--color-4, #ef4136)', fontWeight: 600, marginTop: '2px', lineHeight: 1.2, display: 'block' }}>
                                    {priceWarnings.find(w => w.tableId === table.id && w.rowId === row.id).message}
                                  </span>
                                )}
                                </>
                              )}
                            </td>
                            <td className="pc-td-computed">
                              {calc.totalAssessments.toLocaleString("en-IN")}
                            </td>
                            <td className="pc-td-computed pc-td-highlight">
                              {formatCurrency(parseFloat(calc.pricePerStudent))}
                            </td>
                            <td className="pc-td-computed pc-td-total">
                              {formatCurrency(calc.netPrice)}
                            </td>
                            <td className="pc-td-action">
                              {!isGeneratingPdf && (
                                <button
                                  className="pc-remove-btn"
                                  onClick={() => removeRow(table.id, row.id)}
                                  disabled={table.rows.length === 1}
                                  title="Remove row"
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {!isGeneratingPdf && (
                    <button
                      className="pc-add-row-btn"
                      onClick={() => addRow(table.id)}
                      style={{ marginTop: "0.5rem", fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
                    >
                      <FaPlus /> Add Level
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {!isGeneratingPdf && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
              <button 
                onClick={addTable}
                className="pc-add-row-btn" 
                style={{ background: 'var(--color-1)', color: '#fff', border: 'none', padding: '0.8rem 1.5rem', fontSize: '1rem', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
              >
                <FaPlus /> Add Table Group
              </button>
            </div>
          )}

          {/* Summary Card */}
          <div className="pc-summary" style={{ background: "var(--bg-color)", borderRadius: "16px", padding: "1.5rem" }}>
            <div className="pc-summary-grid">
              <div className="pc-summary-item">
                <span className="pc-summary-label">Total Tables</span>
                <span className="pc-summary-value">{tables.length}</span>
              </div>
              <div className="pc-summary-item">
                <span className="pc-summary-label">Total Students</span>
                <span className="pc-summary-value">
                  {totalStudents.toLocaleString("en-IN")}
                </span>
              </div>
              <div className="pc-summary-item">
                <span className="pc-summary-label">Total Assessments</span>
                <span className="pc-summary-value">
                  {totalAssessmentsAll.toLocaleString("en-IN")}
                </span>
              </div>
              <div className="pc-summary-item">
                <span className="pc-summary-label">Discount</span>
                <div
                  className="pc-discount-input-wrap"
                  style={{
                    marginTop: "0.25rem",
                    padding: "0.4rem 0.6rem",
                    background: "#fff",
                  }}
                >
                  <input
                    type="number"
                    className="pc-discount-input"
                    value={discount}
                    min="0"
                    max="100"
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      if (rawValue === "") {
                        setDiscount("");
                        return;
                      }
                      let val = parseFloat(rawValue);
                      if (isNaN(val)) return;
                      if (val > 100) val = 100;
                      if (val < 0) val = 0;
                      setDiscount(val);
                    }}
                  />
                  <span className="pc-discount-suffix">%</span>
                </div>
              </div>
            </div>

            {/* Budget Fit Section */}
            {!isGeneratingPdf && (
              <div className="pc-budget-section" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem 1.25rem',
                background: 'linear-gradient(135deg, rgba(20,184,166,0.06), rgba(141,198,63,0.06))',
                border: '1.5px solid rgba(20,184,166,0.2)',
                borderRadius: '14px',
                marginBottom: '1.25rem',
                flexWrap: 'wrap',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '140px' }}>
                  <FaBullseye style={{ color: 'var(--color-3)', fontSize: '1.1rem' }} />
                  <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--font-color)' }}>
                    Fit to Budget
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: '200px' }}>
                  <span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--gray-500)' }}>{"\u20b9"}</span>
                  <input
                    type="number"
                    className="pc-input"
                    placeholder="Enter target budget amount"
                    value={targetBudget}
                    min="0"
                    onChange={(e) => {
                      setTargetBudget(e.target.value);
                      setBudgetFeedback(null);
                    }}
                    style={{ flex: 1, fontSize: '1rem', fontWeight: 600 }}
                  />
                </div>
                <button
                  onClick={fitToBudget}
                  className="pc-add-row-btn"
                  style={{
                    background: 'var(--color-3)',
                    color: '#fff',
                    border: 'none',
                    padding: '0.65rem 1.25rem',
                    fontSize: '0.9rem',
                    borderRadius: '10px',
                    boxShadow: '0 4px 12px rgba(20,184,166,0.25)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <FaBullseye /> Apply
                </button>
                {budgetFeedback && (() => {
                  const palette = {
                    error: {
                      bg: 'rgba(239,65,54,0.07)',
                      border: 'rgba(239,65,54,0.25)',
                      color: '#c0392b',
                      icon: <FaExclamationTriangle />,
                      accent: '#ef4136',
                    },
                    info: {
                      bg: 'rgba(240,154,33,0.08)',
                      border: 'rgba(240,154,33,0.3)',
                      color: '#7c5e1e',
                      icon: <FaInfoCircle />,
                      accent: '#f09a21',
                    },
                    success: {
                      bg: 'rgba(20,184,166,0.08)',
                      border: 'rgba(20,184,166,0.3)',
                      color: '#0f766e',
                      icon: <FaCheckCircle />,
                      accent: '#14b8a6',
                    },
                  };
                  const p = palette[budgetFeedback.type] || palette.info;
                  return (
                    <div style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: p.bg,
                      border: `1.5px solid ${p.border}`,
                      borderRadius: '12px',
                      marginTop: '0.5rem',
                      animation: 'pc-feedback-slide-in 0.35s cubic-bezier(0.22,1,0.36,1)',
                      position: 'relative',
                    }}>
                      {/* Close button */}
                      <button
                        onClick={() => setBudgetFeedback(null)}
                        style={{
                          position: 'absolute',
                          top: '0.5rem',
                          right: '0.5rem',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: p.color,
                          opacity: 0.5,
                          fontSize: '0.75rem',
                          padding: '0.2rem',
                          lineHeight: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        title="Dismiss"
                      >
                        <FaTimes />
                      </button>
                      {/* Header row */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: budgetFeedback.details ? '0.5rem' : 0,
                      }}>
                        <span style={{ color: p.accent, fontSize: '1.05rem', flexShrink: 0, display: 'flex' }}>
                          {p.icon}
                        </span>
                        <span style={{
                          color: p.color,
                          fontSize: '0.88rem',
                          fontWeight: 700,
                          lineHeight: 1.35,
                          paddingRight: '1.25rem',
                        }}>
                          {budgetFeedback.message}
                        </span>
                      </div>
                      {/* Detail lines */}
                      {budgetFeedback.details && budgetFeedback.details.length > 0 && (
                        <div style={{
                          paddingLeft: '1.55rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.2rem',
                        }}>
                          {budgetFeedback.details.map((line, i) => (
                            <span key={i} style={{
                              fontSize: '0.82rem',
                              color: p.color,
                              fontWeight: line.startsWith('  •') ? 600 : 500,
                              opacity: line.startsWith('  •') ? 0.9 : 0.8,
                              lineHeight: 1.5,
                            }}>
                              {line}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="pc-total-school">
              <div className="pc-total-school-label">
                <FaSchool />
                Total Price for School
              </div>
              <div className="pc-total-school-amounts">
                {totalPriceBeforeDiscount > totalPriceForSchool && (
                  <div className="pc-total-school-original">
                    <span className="pc-original-label">
                      Price Before Discount:
                    </span>
                    <span className="pc-original-amount">
                      {formatCurrency(Math.round(totalPriceBeforeDiscount))}
                    </span>
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: "0.15rem",
                  }}
                >
                  <div className="pc-total-school-amount">
                    {totalPriceBeforeDiscount > totalPriceForSchool && (
                      <span className="pc-original-label">
                        Price After Discount:
                      </span>
                    )}
                    {formatCurrency(Math.round(totalPriceForSchool))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons — outside pdf-content so they won't be captured */}
        <div
          style={{
            maxWidth: "1200px",
            margin: "1rem auto 0",
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.75rem",
          }}
        >
          <button
            onClick={() => setShowQuotationModal(true)}
            className="pc-add-row-btn"
            title={hasInvalidPrices ? "Fix invalid Price Per Assessment values before generating" : ""}
            style={{ background: "#fff", padding: "0.6rem 1rem", opacity: 1, cursor: 'pointer' }}
          >
            <FaFileInvoiceDollar style={{ color: "var(--color-1)" }} /> Generate
            Quotation
          </button>
          {hasInvalidPrices && (
            <span style={{ color: 'var(--color-4, #ef4136)', fontSize: '0.8rem', fontWeight: 600, alignSelf: 'center' }}>
              ⚠ Fix invalid prices first
            </span>
          )}
          {!isGeneratingPdf ? (
            <button
              onClick={handleDownloadPdf}
              className="pc-add-row-btn"
              title={hasInvalidPrices ? "Fix invalid Price Per Assessment values before saving" : ""}
              style={{ background: "#fff", padding: "0.6rem 1rem", opacity: 1, cursor: 'pointer' }}
            >
              <i
                className="fa-solid fa-file-pdf"
                style={{ color: "#ef4444", fontSize: "1.1rem" }}
              ></i>{" "}
              Save as PDF
            </button>
          ) : (
            <button
              disabled
              className="pc-add-row-btn"
              style={{
                background: "#fff",
                padding: "0.6rem 1rem",
                opacity: 0.7,
              }}
            >
              <i
                className="fa-solid fa-spinner fa-spin"
                style={{ color: "var(--color-1)", fontSize: "1.1rem" }}
              ></i>{" "}
              Generating...
            </button>
          )}
        </div>

        {/* Quotation Modal */}
        <Modal
          isOpen={showQuotationModal}
          onClose={() => setShowQuotationModal(false)}
          maxWidth="520px"
        >
          <h2
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginBottom: "0.25rem",
            }}
          >
            Generate Quotation
          </h2>
          <p className="modal-description" style={{ marginBottom: "1.25rem" }}>
            Enter recipient details for the quotation document.
          </p>
          <div className="form-group">
            <label>Recipient Name</label>
            <input
              type="text"
              placeholder="e.g. Dr. Rajesh Kumar"
              value={quotationRecipient.name}
              onChange={(e) =>
                setQuotationRecipient({
                  ...quotationRecipient,
                  name: e.target.value,
                })
              }
            />
          </div>
          <div className="form-group">
            <label>Designation</label>
            <input
              type="text"
              placeholder="e.g. Principal"
              value={quotationRecipient.designation}
              onChange={(e) =>
                setQuotationRecipient({
                  ...quotationRecipient,
                  designation: e.target.value,
                })
              }
            />
          </div>
          <div className="form-group">
            <label>School Name</label>
            <input
              type="text"
              placeholder="e.g. Delhi Public School, Whitefield"
              value={quotationRecipient.schoolName}
              onChange={(e) =>
                setQuotationRecipient({
                  ...quotationRecipient,
                  schoolName: e.target.value,
                })
              }
            />
          </div>
          <div className="form-group">
            <label>School Address</label>
            <textarea
              rows="1"
              style={{ height: "50px" }}
              placeholder="e.g. ITPL Road, Whitefield, Bangalore - 560066"
              value={quotationRecipient.schoolAddress}
              onChange={(e) =>
                setQuotationRecipient({
                  ...quotationRecipient,
                  schoolAddress: e.target.value,
                })
              }
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.75rem",
              marginTop: "1rem",
            }}
          >
            <button className="btn-submit" onClick={handleGenerateQuotation}>
              Generate & Download
            </button>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default PriceCalculator;
